import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Users, Clock, MapPin, Video, Building } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YogaClassesPage = () => {
  const { getAuthHeader } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedClass, setSelectedClass] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    booking_date: '',
    booking_time: ''
  });

  useEffect(() => {
    fetchClasses();
  }, [filter]);

  const fetchClasses = async () => {
    try {
      const params = filter !== 'all' ? { type: filter } : {};
      const response = await axios.get(`${API}/yoga/classes`, { params });
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/yoga/book`,
        {
          class_id: selectedClass.id,
          ...bookingData
        },
        { headers: getAuthHeader() }
      );
      toast.success('Yoga class booked successfully!');
      setShowBooking(false);
      setBookingData({ booking_date: '', booking_time: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="yoga-classes-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Yoga Classes
          </h1>
          <p className="text-lg text-gray-600">Dosha-specific yoga practices for optimal health</p>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 mb-8">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            data-testid="filter-all-yoga"
          >
            All Classes
          </Button>
          <Button 
            variant={filter === 'digital' ? 'default' : 'outline'}
            onClick={() => setFilter('digital')}
            data-testid="filter-digital"
          >
            <Video className="h-4 w-4 mr-2" />
            Digital Classes
          </Button>
          <Button 
            variant={filter === 'center' ? 'default' : 'outline'}
            onClick={() => setFilter('center')}
            data-testid="filter-center"
          >
            <Building className="h-4 w-4 mr-2" />
            Center-Based
          </Button>
        </div>

        {/* Classes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {classes.map((yogaClass) => (
            <Card key={yogaClass.id} className="card-lift">
              <div className="h-48 bg-muted overflow-hidden">
                <img src={yogaClass.image_url} alt={yogaClass.title} className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full capitalize">
                    {yogaClass.level}
                  </span>
                  <span className="text-xs text-gray-600 flex items-center">
                    {yogaClass.type === 'digital' ? <Video className="h-3 w-3 mr-1" /> : <Building className="h-3 w-3 mr-1" />}
                    {yogaClass.type}
                  </span>
                </div>

                <h3 className="text-xl font-semibold mb-2">{yogaClass.title}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{yogaClass.description}</p>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    {yogaClass.instructor}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    {yogaClass.duration_minutes} minutes
                  </div>
                  {yogaClass.center_location && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      {yogaClass.center_location}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-primary">₹{yogaClass.price}</div>
                  <Button
                    onClick={() => {
                      setSelectedClass(yogaClass);
                      setShowBooking(true);
                    }}
                    data-testid={`book-yoga-${yogaClass.id}`}
                  >
                    Book Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {classes.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500">No classes found</p>
          </div>
        )}
      </main>

      <Footer />

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent data-testid="yoga-booking-dialog">
          <DialogHeader>
            <DialogTitle>Book Yoga Class</DialogTitle>
            {selectedClass && (
              <p className="text-sm text-gray-600">{selectedClass.title} - {selectedClass.instructor}</p>
            )}
          </DialogHeader>

          <form onSubmit={handleBooking} className="space-y-4">
            <div>
              <Label htmlFor="booking_date">Preferred Date</Label>
              <Input
                id="booking_date"
                type="date"
                value={bookingData.booking_date}
                onChange={(e) => setBookingData({ ...bookingData, booking_date: e.target.value })}
                required
                data-testid="yoga-date-input"
              />
            </div>

            <div>
              <Label htmlFor="booking_time">Preferred Time</Label>
              <Input
                id="booking_time"
                type="time"
                value={bookingData.booking_time}
                onChange={(e) => setBookingData({ ...bookingData, booking_time: e.target.value })}
                required
                data-testid="yoga-time-input"
              />
            </div>

            {selectedClass && (
              <div className="bg-muted p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Amount</span>
                  <span className="text-2xl font-bold text-primary">₹{selectedClass.price}</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Commission: {selectedClass.type === 'digital' ? '15%' : '20%'}
                </p>
              </div>
            )}

            <Button type="submit" className="w-full" data-testid="confirm-yoga-booking">
              Confirm Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YogaClassesPage;